import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, Activity, Sparkles, BarChart3, Calculator, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ScatterChart, Scatter } from 'recharts';

interface FrequencyEntry {
  date: string;
  frequency: number;
  reading_quality: number;
  vibe_range: string;
  activities: string[];
  mood: string;
  observations?: string;
  visualization_notes?: string;
}

const activityLabels: { [key: string]: string } = {
  meditation: 'Meditation',
  sitting_in_power: 'Sitting in the Power',
  nature: 'Time in Nature',
  music: 'High Vibe Music',
  gratitude: 'Gratitude Practice',
  movement: 'Movement/Dance',
  laughter: 'Joy/Laughter',
  service: 'Acts of Service',
  creativity: 'Creative Expression',
  other: 'Other Practice'
};

export default function VibrationalFrequencyInsights() {
  const [data, setData] = useState<FrequencyEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadHistoricalData();
  }, []);

  const loadHistoricalData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: entries, error } = await supabase
        .from('vibrational_frequency')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', thirtyDaysAgo.toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (error) throw error;
      setData(entries || []);
    } catch (error) {
      console.error('Error loading frequency data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateCorrelation = () => {
    if (data.length < 2) return null;
    
    const n = data.length;
    const sumX = data.reduce((sum, d) => sum + d.frequency, 0);
    const sumY = data.reduce((sum, d) => sum + d.reading_quality, 0);
    const sumXY = data.reduce((sum, d) => sum + (d.frequency * d.reading_quality), 0);
    const sumX2 = data.reduce((sum, d) => sum + (d.frequency * d.frequency), 0);
    const sumY2 = data.reduce((sum, d) => sum + (d.reading_quality * d.reading_quality), 0);
    
    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
    
    // Avoid division by zero
    if (denominator === 0) return null;
    
    const correlation = numerator / denominator;
    
    return isNaN(correlation) ? null : correlation;
  };

  const getCorrelationStats = () => {
    if (data.length < 2) return null;
    
    const n = data.length;
    const sumX = data.reduce((sum, d) => sum + d.frequency, 0);
    const sumY = data.reduce((sum, d) => sum + d.reading_quality, 0);
    const sumXY = data.reduce((sum, d) => sum + (d.frequency * d.reading_quality), 0);
    const sumX2 = data.reduce((sum, d) => sum + (d.frequency * d.frequency), 0);
    const sumY2 = data.reduce((sum, d) => sum + (d.reading_quality * d.reading_quality), 0);
    const meanX = sumX / n;
    const meanY = sumY / n;
    
    return {
      n,
      sumX,
      sumY,
      sumXY,
      sumX2,
      sumY2,
      meanX,
      meanY,
      numerator: n * sumXY - sumX * sumY,
      denominator: Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY))
    };
  };

  const getActivityStats = () => {
    const activityCounts: { [key: string]: number } = {};
    const activityFrequencies: { [key: string]: number[] } = {};
    
    data.forEach(entry => {
      entry.activities.forEach(activity => {
        activityCounts[activity] = (activityCounts[activity] || 0) + 1;
        if (!activityFrequencies[activity]) activityFrequencies[activity] = [];
        activityFrequencies[activity].push(entry.frequency);
      });
    });

    return Object.keys(activityCounts).map(activity => ({
      activity: activityLabels[activity] || activity,
      count: activityCounts[activity],
      avgFrequency: activityFrequencies[activity].reduce((a, b) => a + b, 0) / activityFrequencies[activity].length
    })).sort((a, b) => b.avgFrequency - a.avgFrequency);
  };

  const getVibeDistribution = () => {
    const distribution = { low: 0, mid: 0, high: 0 };
    data.forEach(entry => {
      distribution[entry.vibe_range as keyof typeof distribution]++;
    });
    
    return [
      { name: 'Low Vibe (1-3)', value: distribution.low, fill: 'hsl(var(--destructive))' },
      { name: 'Mid Vibe (4-7)', value: distribution.mid, fill: 'hsl(var(--accent))' },
      { name: 'High Vibe (8-10)', value: distribution.high, fill: 'hsl(var(--primary))' }
    ];
  };

  const correlation = calculateCorrelation();
  const correlationStats = getCorrelationStats();
  const activityStats = getActivityStats();
  const vibeDistribution = getVibeDistribution();
  const avgFrequency = data.length > 0 ? data.reduce((sum, d) => sum + d.frequency, 0) / data.length : 0;
  const avgReadingQuality = data.length > 0 ? data.reduce((sum, d) => sum + d.reading_quality, 0) / data.length : 0;
  
  // Data quality checks
  const dataQuality = {
    hasMinimumData: data.length >= 7,
    hasVariance: new Set(data.map(d => d.frequency)).size > 1 && new Set(data.map(d => d.reading_quality)).size > 1,
    hasActivities: data.some(d => d.activities && d.activities.length > 0),
    completionRate: data.filter(d => d.frequency && d.reading_quality).length / data.length
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Loading insights...</p>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Development Insights
          </CardTitle>
          <CardDescription>
            Start tracking your frequency daily to see patterns and correlations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No data yet. Log your vibrational frequency regularly to build insights about your psychic development journey.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Your Development Journey
        </CardTitle>
        <CardDescription>
          Track patterns, correlations, and growth in your psychic development
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-4 flex-wrap">
          <Badge variant={dataQuality.hasMinimumData ? "default" : "secondary"}>
            {data.length} days tracked
          </Badge>
          <Badge variant={dataQuality.hasVariance ? "default" : "secondary"}>
            {dataQuality.hasVariance ? "Good variance" : "Need more variance"}
          </Badge>
          <Badge variant={dataQuality.hasActivities ? "default" : "secondary"}>
            {dataQuality.hasActivities ? "Activities logged" : "Log activities"}
          </Badge>
          <Badge variant={dataQuality.completionRate === 1 ? "default" : "secondary"}>
            {(dataQuality.completionRate * 100).toFixed(0)}% complete data
          </Badge>
        </div>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="journal">Journal</TabsTrigger>
            <TabsTrigger value="correlation">Correlation</TabsTrigger>
            <TabsTrigger value="activities">Activities</TabsTrigger>
            <TabsTrigger value="patterns">Patterns</TabsTrigger>
            <TabsTrigger value="calculations">Math</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-xs text-muted-foreground mb-1">Average Frequency</p>
                <p className="text-2xl font-bold text-primary">{avgFrequency.toFixed(1)}/10</p>
              </div>
              <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-xs text-muted-foreground mb-1">Average Reading Quality</p>
                <p className="text-2xl font-bold text-primary">{avgReadingQuality.toFixed(1)}/10</p>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Frequency Over Time
              </h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="date" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={[0, 10]} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="frequency" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))' }}
                    name="Frequency"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="reading_quality" 
                    stroke="hsl(var(--secondary))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--secondary))' }}
                    name="Reading Quality"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="journal" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-primary" />
                  Frequency Journal ({data.length} entries)
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Review your past observations, patterns, and development journey
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {data.slice().reverse().map((entry) => {
                const isExpanded = expandedEntries.has(entry.date);
                const vibeColor = 
                  entry.vibe_range === 'high' ? 'text-primary' :
                  entry.vibe_range === 'mid' ? 'text-accent' :
                  'text-destructive';
                
                return (
                  <Collapsible
                    key={entry.date}
                    open={isExpanded}
                    onOpenChange={(open) => {
                      const newSet = new Set(expandedEntries);
                      if (open) {
                        newSet.add(entry.date);
                      } else {
                        newSet.delete(entry.date);
                      }
                      setExpandedEntries(newSet);
                    }}
                  >
                    <Card className="overflow-hidden">
                      <CollapsibleTrigger asChild>
                        <div className="p-4 cursor-pointer hover:bg-muted/50 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <p className="font-medium">
                                  {new Date(entry.date).toLocaleDateString('en-US', { 
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </p>
                                <Badge variant="outline" className={vibeColor}>
                                  {entry.vibe_range.toUpperCase()} VIBE
                                </Badge>
                              </div>
                              
                              <div className="flex items-center gap-4 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Frequency:</span>
                                  <span className="ml-1 font-bold text-primary">{entry.frequency}/10</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Reading Quality:</span>
                                  <span className="ml-1 font-bold text-primary">{entry.reading_quality}/10</span>
                                </div>
                                {entry.mood && (
                                  <div>
                                    <span className="text-muted-foreground">Mood:</span>
                                    <span className="ml-1 capitalize">{entry.mood.replace('_', ' ')}</span>
                                  </div>
                                )}
                              </div>
                              
                              {entry.activities && entry.activities.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {entry.activities.map((activity, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {activityLabels[activity] || activity}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            <Button variant="ghost" size="sm" className="ml-2">
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className="px-4 pb-4 pt-2 space-y-3 border-t">
                          {entry.observations && (
                            <div className="p-3 bg-muted/30 rounded-lg">
                              <p className="text-xs font-medium text-muted-foreground mb-2">
                                Observations & Patterns
                              </p>
                              <p className="text-sm whitespace-pre-wrap">{entry.observations}</p>
                            </div>
                          )}
                          
                          {entry.visualization_notes && (
                            <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                              <p className="text-xs font-medium text-primary mb-2">
                                Frequency Visualisation Practice
                              </p>
                              <p className="text-sm whitespace-pre-wrap">{entry.visualization_notes}</p>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-3 pt-2">
                            <div className="p-2 bg-background rounded text-center">
                              <p className="text-xs text-muted-foreground">Vibe Range</p>
                              <p className={`text-lg font-bold ${vibeColor}`}>
                                {entry.vibe_range === 'high' ? '8-10' :
                                 entry.vibe_range === 'mid' ? '4-7' : '1-3'}
                              </p>
                            </div>
                            <div className="p-2 bg-background rounded text-center">
                              <p className="text-xs text-muted-foreground">Activities Done</p>
                              <p className="text-lg font-bold text-primary">
                                {entry.activities?.length || 0}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="correlation" className="space-y-4">
            {correlation !== null && (
              <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
                <h4 className="font-medium mb-2">Frequency ↔ Reading Quality Correlation</h4>
                <p className="text-3xl font-bold text-accent mb-2">
                  {(correlation * 100).toFixed(0)}%
                </p>
                <p className="text-sm text-muted-foreground">
                  {correlation > 0.7 ? 'Strong positive correlation! Higher frequency consistently leads to better readings.' :
                   correlation > 0.4 ? 'Moderate positive correlation. Your frequency impacts reading quality.' :
                   correlation > 0 ? 'Weak positive correlation. Some connection between frequency and quality.' :
                   'No clear correlation yet. Keep tracking to identify patterns.'}
                </p>
              </div>
            )}

            <div>
              <h4 className="text-sm font-medium mb-3">Frequency vs Reading Quality</h4>
              <ResponsiveContainer width="100%" height={250}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="frequency" 
                    name="Frequency"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    domain={[0, 10]}
                  />
                  <YAxis 
                    dataKey="reading_quality" 
                    name="Reading Quality"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    domain={[0, 10]}
                  />
                  <Tooltip 
                    cursor={{ strokeDasharray: '3 3' }}
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                  />
                  <Scatter 
                    data={data} 
                    fill="hsl(var(--primary))"
                    name="Sessions"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="activities" className="space-y-4">
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Most Effective Practices
              </h4>
              {activityStats.slice(0, 5).map((stat, idx) => (
                <div key={idx} className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-sm font-medium">{stat.activity}</p>
                    <span className="text-xs text-primary font-bold">
                      {stat.avgFrequency.toFixed(1)} avg
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Used {stat.count} times</span>
                  </div>
                </div>
              ))}
            </div>

            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Activity Frequency
              </h4>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={activityStats} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis 
                    dataKey="activity" 
                    type="category" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={11}
                    width={120}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" name="Times Used" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="patterns" className="space-y-4">
            <div>
              <h4 className="text-sm font-medium mb-3">Vibrational Range Distribution</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={vibeDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                  />
                  <Bar dataKey="value" name="Days">
                    {vibeDistribution.map((entry, index) => (
                      <Bar key={`bar-${index}`} dataKey="value" fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="p-4 bg-muted/30 rounded-lg space-y-2">
              <h4 className="font-medium text-sm">Development Recommendations</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {vibeDistribution[0].value > data.length * 0.3 && (
                  <li className="flex gap-2">
                    <span>•</span>
                    <span>You're experiencing low vibe states frequently. Focus on grounding practices and self-care.</span>
                  </li>
                )}
                {activityStats[0]?.avgFrequency > 7 && (
                  <li className="flex gap-2">
                    <span>•</span>
                    <span>"{activityStats[0].activity}" consistently raises your frequency - make it a daily practice!</span>
                  </li>
                )}
                {correlation && correlation > 0.5 && (
                  <li className="flex gap-2">
                    <span>•</span>
                    <span>Strong correlation detected! Prioritise frequency-raising activities before psychic work.</span>
                  </li>
                )}
                {data.length >= 7 && (
                  <li className="flex gap-2">
                    <span>•</span>
                    <span>Great consistency! {data.length} days tracked. Keep building this awareness.</span>
                  </li>
                )}
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="calculations" className="space-y-4">
            <div className="p-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg border border-primary/20">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Calculator className="h-4 w-4 text-primary" />
                Your Frequency-to-Reading Connection
              </h4>
              
              {correlationStats ? (
                <div className="space-y-4">
                  <div className="text-center p-4 bg-background/50 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">Your Connection Strength</p>
                    <div className="text-5xl font-bold text-primary mb-2">
                      {(Math.abs(correlation || 0) * 100).toFixed(0)}%
                    </div>
                    <p className="text-sm font-medium">
                      {correlation && correlation > 0.7 ? '🌟 Powerful Connection!' :
                       correlation && correlation > 0.4 ? '✨ Strong Connection' :
                       correlation && correlation > 0.1 ? '💫 Building Connection' :
                       '🌱 Early Stages'}
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="p-3 bg-background/50 rounded-lg">
                      <p className="text-sm font-medium mb-2">What This Means For You:</p>
                      {correlation && correlation > 0.7 && (
                        <p className="text-sm text-muted-foreground">
                          Your vibrational frequency has a strong, consistent impact on your psychic reading quality. When you raise your vibe, your readings improve significantly. This is powerful evidence of your energetic awareness!
                        </p>
                      )}
                      {correlation && correlation > 0.4 && correlation <= 0.7 && (
                        <p className="text-sm text-muted-foreground">
                          There's a clear pattern: higher frequencies tend to lead to better readings. Keep prioritising frequency-raising practices before psychic work. You're developing real energetic mastery.
                        </p>
                      )}
                      {correlation && correlation > 0.1 && correlation <= 0.4 && (
                        <p className="text-sm text-muted-foreground">
                          You're starting to see how your frequency affects your readings. As you track more data and become more consistent with frequency-raising practices, this connection will strengthen.
                        </p>
                      )}
                      {correlation && correlation <= 0.1 && (
                        <p className="text-sm text-muted-foreground">
                          Every psychic is unique. Your readings may be influenced by factors beyond just frequency - like timing, intention setting, or other energetic practices. Keep exploring what works best for you.
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-background/50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground mb-1">Days Tracked</p>
                        <p className="text-2xl font-bold text-primary">{correlationStats.n}</p>
                      </div>
                      <div className="p-3 bg-background/50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground mb-1">Your Average Frequency</p>
                        <p className="text-2xl font-bold text-accent">{correlationStats.meanX.toFixed(1)}/10</p>
                      </div>
                    </div>

                    <div className="p-3 bg-accent/10 rounded-lg border border-accent/20">
                      <p className="text-sm font-medium mb-2">💡 How to Use This:</p>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• When your connection % is high: Trust your frequency-raising routine - it works!</li>
                        <li>• Before important readings: Use your most effective practices to raise your vibe</li>
                        <li>• When readings feel off: Check your frequency first - you might need grounding</li>
                        <li>• Track consistently to see how life changes affect your psychic clarity</li>
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Start tracking for at least 2 days to see how your frequency impacts your readings!
                </p>
              )}
            </div>

            <div className="p-4 bg-gradient-to-br from-secondary/10 to-primary/10 rounded-lg border border-secondary/20">
              <h4 className="font-medium mb-3">Your Most Effective Practices</h4>
              <p className="text-sm text-muted-foreground mb-3">
                See which activities consistently raise your frequency the most:
              </p>
              
              {activityStats.length > 0 ? (
                <div className="space-y-2">
                  {activityStats.slice(0, 5).map((stat, idx) => (
                    <div key={idx} className="p-3 bg-background/50 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <p className="font-medium">{stat.activity}</p>
                        <Badge variant="outline" className="text-primary">
                          {stat.avgFrequency.toFixed(1)}/10 avg
                        </Badge>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2 mb-1">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{ width: `${(stat.avgFrequency / 10) * 100}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Used {stat.count} {stat.count === 1 ? 'time' : 'times'} • 
                        {stat.avgFrequency >= 8 ? ' 🌟 Powerful practice!' :
                         stat.avgFrequency >= 6 ? ' ✨ Very effective' :
                         stat.avgFrequency >= 4 ? ' 💫 Helpful' :
                         ' 🌱 Explore other options'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Start logging activities with your frequency entries to discover what works best for you!
                </p>
              )}
            </div>

            <div className="p-4 bg-muted/30 rounded-lg border border-border">
              <h4 className="font-medium mb-3">Your Development Journey Quality</h4>
              <div className="space-y-3">
                <div className="p-3 bg-background rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Days Tracked</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold">{correlationStats?.n || 0}</span>
                      <Badge variant={dataQuality.hasMinimumData ? "default" : "secondary"} className="text-xs">
                        {dataQuality.hasMinimumData ? "✓ Excellent" : "Need 7+"}
                      </Badge>
                    </div>
                  </div>
                  {!dataQuality.hasMinimumData && (
                    <p className="text-xs text-muted-foreground">
                      Track for 7+ days to unlock reliable patterns and correlations
                    </p>
                  )}
                </div>

                <div className="p-3 bg-background rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Data Variety</span>
                    <Badge variant={dataQuality.hasVariance ? "default" : "secondary"}>
                      {dataQuality.hasVariance ? "✓ Good Range" : "Need Variety"}
                    </Badge>
                  </div>
                  {!dataQuality.hasVariance && (
                    <p className="text-xs text-muted-foreground">
                      Try tracking on both high and low energy days for better insights
                    </p>
                  )}
                </div>

                <div className="p-3 bg-background rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Activity Tracking</span>
                    <Badge variant={dataQuality.hasActivities ? "default" : "secondary"}>
                      {dataQuality.hasActivities ? "✓ Active" : "Start Logging"}
                    </Badge>
                  </div>
                  {!dataQuality.hasActivities && (
                    <p className="text-xs text-muted-foreground">
                      Log activities to discover which practices work best for you
                    </p>
                  )}
                </div>

                <div className="p-3 bg-background rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Completeness</span>
                    <Badge variant={dataQuality.completionRate === 1 ? "default" : "secondary"}>
                      {(dataQuality.completionRate * 100).toFixed(0)}%
                    </Badge>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${dataQuality.completionRate * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
